import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { format } from 'date-fns'

import { useNotifications } from 'app/application/application'
import { getFormerDay } from 'app/common/presentation/getFormerDay'
import { HomeIcon } from 'app/common/presentation/icons'
import { useUser } from 'app/user/application'

import { Divider } from 'components'

import { useAnalytics } from '../application'
import { AudiencesDayRevenue, DaySessions } from '../types'
import styles from './DashboardPage.module.scss'
import { ConversionType, MatchingRatio, Revenue, Sessions, TopAudiences, Treatments } from './tiles'
import Empty from './tiles/Empty'

const days = 30
const periodFormat = 'MMM dd, yyyy'
export const tooltipDateFormat = 'MMM dd'
const today = new Date()
const daysBefore = getFormerDay(days)

const Dashboard = () => {
  const { fetchRevenues, fetchSessions } = useAnalytics()
  const { createNotification, te } = useNotifications()
  const { t } = useTranslation('dashboard')
  const { workspace } = useUser()

  const [revenues, setRevenues] = useState<AudiencesDayRevenue[]>([])
  const [sessions, setSessions] = useState<DaySessions[]>([])

  useEffect(() => {
    void fetchRevenues(days).then((result) =>
      result.ok
        ? setRevenues(result.val)
        : createNotification({
            message: result.val.message,
            title: te(`http.${result.val.code}`),
            type: 'error',
          }),
    )
  }, [createNotification, fetchRevenues, te])
  useEffect(() => {
    void fetchSessions(days).then((result) =>
      result.ok
        ? setSessions(result.val)
        : createNotification({
            message: result.val.message,
            title: te(`http.${result.val.code}`),
            type: 'error',
          }),
    )
  }, [createNotification, fetchSessions, te])

  const workspaceName = workspace?.name ?? ''

  const tileTranslate = useCallback(
    (key: string, options?: Record<string, unknown>) => t(`tiles.${key}`, options),
    [t],
  )

  return (
    <div className={styles.dashboard}>
      <div className={styles.pageHeader}>
        <div className={styles.icon}>
          <HomeIcon />
        </div>
        <div className={styles.welcome}>
          <h1 className={styles.title}>
            {t('title', { count: workspaceName ? 1 : 0, workspaceName })}
          </h1>
          <div className={styles.date}>
            {t('period', {
              daysBefore: format(daysBefore, periodFormat),
              today: format(today, periodFormat),
            })}
          </div>
        </div>
      </div>
      <div className={styles.tilesGrid}>
        <div>
          <Revenue days={days} revenues={revenues} translate={tileTranslate} />
          <TopAudiences days={days} revenues={revenues} translate={tileTranslate} />
        </div>
        <Divider />
        <div>
          <Sessions days={days} sessions={sessions} translate={tileTranslate} />
          <Treatments days={days} sessions={sessions} translate={tileTranslate} />
          <ConversionType days={days} translate={tileTranslate} />
        </div>
        <Divider />
        <div>
          <MatchingRatio days={days} translate={tileTranslate} />
          <Empty />
        </div>
      </div>
    </div>
  )
}

export default Dashboard
